<template>
  <v-container fluid>
    <v-row>
      <v-col class="px-1" cols="12" md="5">
        <ContainerWithLabel label="Kanal" class="text-caption">
          <template #append-content>
            <v-btn
              v-tooltip:right="
                'Definiert, welchem Kanal diese Regel zugeordnet ist'
              "
              density="compact"
              size="small"
              color="primary"
              variant="text"
              icon="mdi-help-circle-outline"
            />
          </template>
          <v-select
            v-model="configRef.rules[props.ruleNr].channel.value"
            size="small"
            density="compact"
            color="primary"
            width="225px"
            hide-details="auto"
            :items="
              SWW_CHANNELS.map((channel) => ({
                value: channel.value,
                title: channel.name,
              }))
            "
          />
        </ContainerWithLabel>
      </v-col>
      <v-col class="px-1">
        <ContainerWithLabel label="Aktion" class="text-caption">
          <template #append-content>
            <v-btn
              v-tooltip:right="
                `Definiert, ob in Abhängigkeit der Bedingungen der Befehl bei Überschreitung
              oder Unterschreitung des oberen bzw. unteren Schwellwertes ausgeführt werden soll.`
              "
              density="compact"
              color="primary"
              size="small"
              variant="text"
              icon="mdi-help-circle-outline"
            />
          </template>
          <v-select
            v-model="action"
            size="small"
            width="225px"
            color="primary"
            density="compact"
            hide-details="auto"
            :items="[
              { value: 0, title: '< unterer Schwellwert' },
              {
                value: 1,
                title: '>= oberer Schwellwert',
              },
            ]"
          />
        </ContainerWithLabel>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="px-1" cols="12" md="5">
        <ContainerWithLabel label="Befehl" class="text-caption">
          <template #append-content>
            <v-btn
              v-tooltip:right="
                `Definiert, welcher Befehl verwendet werden soll`
              "
              density="compact"
              size="small"
              color="primary"
              variant="text"
              icon="mdi-help-circle-outline"
            />
          </template>

          <v-select
            v-model="configRef.rules[props.ruleNr].cmd.value"
            size="small"
            color="primary"
            hide-details="auto"
            density="compact"
            width="225px"
            :items="
              CMD_LIST.map((cmd) => ({
                title: cmd.name,
                value: cmd.value,
              }))
            "
          />
        </ContainerWithLabel>
      </v-col>
      <v-col class="px-1" cols="12" md="5">
        <ContainerWithLabel label="Level" class="text-caption">
          <template #append-content>
            <v-btn
              v-tooltip:right="
                `Definiert, bis zu welcher Sperrstufe das Kommando angewendet wird`
              "
              density="compact"
              size="small"
              color="primary"
              variant="text"
              icon="mdi-help-circle-outline"
            />
          </template>
          <v-select
            v-model="configRef.rules[props.ruleNr].priority.value"
            size="small"
            density="compact"
            width="225px"
            hide-details="auto"
            :items="
              PRIORITIES.map((prio) => ({
                title: prio.name,
                value: prio.value,
              }))
            "
          />
        </ContainerWithLabel>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="px-1 py-0" cols="6" md="2">
        <ContainerWithLabel label="Sperren" class="text-caption">
          <template #append-content>
            <v-btn
              v-tooltip:right="
                `Falls aktiviert, wird die mit 'Level' gewählte Sperrstufe aktiviert`
              "
              density="compact"
              hide-details="auto"
              color="primary"
              size="small"
              variant="text"
              icon="mdi-help-circle-outline"
            />
          </template>
          <v-switch
            v-model="configRef.rules[props.ruleNr].lock.value"
            :true-value="1"
            :false-value="0"
            hide-details
            color="primary"
            size="small"
            density="compact"
          />
        </ContainerWithLabel>
      </v-col>
      <v-col class="px-1 py-0" cols="6" md="2">
        <ContainerWithLabel label="Entsperren" class="text-caption">
          <template #append-content>
            <v-btn
              v-tooltip:right="
                `Falls aktiviert, wird die mit 'Level' gewählte Sperrstufe deaktiviert`
              "
              density="compact"
              color="primary"
              size="small"
              variant="text"
              icon="mdi-help-circle-outline"
            />
            <v-switch
              v-model="configRef.rules[props.ruleNr].unlock.value"
              :true-value="1"
              :false-value="0"
              hide-details
              color="primary"
              size="small"
            />
          </template>
        </ContainerWithLabel>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="px-1 py-1">
        <ContainerWithLabel label="Bedingung" class="text-caption">
          <template #append-content>
            <v-btn
              v-tooltip:right="
                `Definiert unter welchen Bedingungen die oben\nausgewählte Funktion ausgeführt wird.\nLegende:
        x (weiss) = Die Funktion hat keinen Einfluss\n
        >= (grün) = Der obere Schwellwert bei der\nentsprechenden Funktion muss gleich hoch oder\nüberschritten sein.\n
        < (blau) = Der untere Schwellwert bei der\nentsprechenden Funktion muss unterschritten sein.
        `
              "
              density="compact"
              size="small"
              color="primary"
              variant="text"
              icon="mdi-help-circle-outline"
            />
          </template>
        </ContainerWithLabel>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="px-1 py-0">
        <v-btn-group v-if="conditions" divided class="border mb-4">
          <v-btn
            v-for="(condition, index) in conditions"
            :key="index"
            v-tooltip="SWW_CHANNELS[index].name"
            :color="
              condition === 1 ? 'success' : condition === 2 ? 'info' : undefined
            "
            :class="{
              'is-success': condition === 1,
              'is-info': condition === 2,
            }"
            class="button"
            @click="changeCondition(index)"
          >
            <span v-if="condition === 0">x</span>
            <span v-else-if="condition === 1">≧</span>
            <span v-else>&lt;</span>
          </v-btn>
        </v-btn-group>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { onMounted, Ref, ref, watch } from 'vue'

import {
  CMD_LIST,
  PRIORITIES,
  SWW_CHANNELS,
  SwwConfig,
} from '@/apps/brelag/common/models/swwParams'
import ContainerWithLabel from '@/components/common/ContainerWithLabel.vue'
import { reverseString } from '@/util/util'

type SwwRuleProps = {
  rule: SwwConfig['rules'][0]
  ruleNr: number
}

const props = defineProps<SwwRuleProps>()
const configRef = defineModel<SwwConfig>()
const action: Ref<number> = ref(null)
const conditions: Ref<number[]> = ref([])

function parseConditions() {
  let conditionBits = ['0', '0', '0', '0', '0', '0', '0', '0']
  let actionBits = ['0', '0', '0', '0', '0', '0', '0', '0']
  for (let i = 0; i < 8; i++) {
    if (props.rule.channel.value === i) {
      conditionBits[i] = '0'
      if (action.value === 0) {
        actionBits[i] = '0'
      } else {
        actionBits[i] = '1'
      }
    } else {
      if (conditions.value[i] === 0) {
        actionBits[i] = '0'
        conditionBits[i] = '0'
      } else if (conditions.value[i] === 1) {
        actionBits[i] = '1'
        conditionBits[i] = '1'
      } else {
        actionBits[i] = '0'
        conditionBits[i] = '1'
      }
    }
  }
  configRef.value.rules[props.ruleNr].channelCondition.value = parseInt(
    conditionBits.reverse().join(''),
    2
  )
  configRef.value.rules[props.ruleNr].onActive.value = parseInt(
    actionBits.reverse().join(''),
    2
  )
}

function changeCondition(index: number) {
  if (conditions.value[index] === 0 || conditions.value[index] === 1) {
    conditions.value[index] = conditions.value[index] + 1
  } else {
    conditions.value[index] = 0
  }

  parseConditions()
}

watch(action, () => parseConditions())

watch(
  () => configRef.value.rules[props.ruleNr].channel.value,
  () => {
    parseConditions()
  }
)

onMounted(() => {
  let actionBitsString = props.rule?.onActive?.value.toString(2) ?? ''
  actionBitsString = ('00000000' + actionBitsString).substring(
    actionBitsString.length
  )
  actionBitsString = reverseString(actionBitsString)
  let conditionBitsString = props.rule.channelCondition.value.toString(2)
  conditionBitsString = ('00000000' + conditionBitsString).substring(
    conditionBitsString.length
  )
  conditionBitsString = reverseString(conditionBitsString)

  conditions.value = [0, 0, 0, 0, 0, 0, 0, 0]
  for (let i = 0; i < 8; i++) {
    if (props.rule.channel.value === i) {
      if (actionBitsString[i] === '0') {
        action.value = 0
      } else {
        action.value = 1
      }
    } else {
      if (actionBitsString[i] === '0' && conditionBitsString[i] === '0') {
        conditions.value[i] = 0
      } else if (
        actionBitsString[i] === '1' &&
        conditionBitsString[i] === '1'
      ) {
        conditions.value[i] = 1
      } else {
        conditions.value[i] = 2
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.my-tooltip {
  &.tooltip:after {
    white-space: pre;
  }
}
</style>
